import React, { useMemo, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Avatar,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Phone,
  Email,
  LocationOn,
  History as HistoryIcon,
} from '@mui/icons-material';

const fallbackCustomers = [
  {
    customerId: 'CUST001',
    name: 'Rajesh Kumar',
    email: 'rajesh.kumar@example.com',
    mobile: '9876543210',
    city: 'Mumbai',
    state: 'Maharashtra',
    address: '123, Marine Drive, Mumbai, Maharashtra - 400001',
    profession: 'Software Engineer',
    status: 'Active',
    purchaseHistory: [
      {
        id: 'ORD-001',
        date: '2024-11-15',
        project: 'Green Meadows Residency',
        unit: 'Tower A - 2BHK (1200 sqft)',
        amount: 1250000,
        paymentStatus: 'Completed',
        paymentMode: 'Bank Transfer',
      },
      {
        id: 'ORD-012',
        date: '2023-07-04',
        project: 'Lakeview Villas',
        unit: 'Villa Plot 27',
        amount: 2250000,
        paymentStatus: 'Completed',
        paymentMode: 'Cheque',
      },
    ],
  },
  {
    customerId: 'CUST002',
    name: 'Priya Sharma',
    email: 'priya.sharma@example.com',
    mobile: '9123456789',
    city: 'Delhi',
    state: 'Delhi',
    address: '456, Connaught Place, New Delhi, Delhi - 110001',
    profession: 'Business Owner',
    status: 'Active',
    purchaseHistory: [
      {
        id: 'ORD-021',
        date: '2025-01-18',
        project: 'Metro Plaza',
        unit: 'Shop No. 12, Ground Floor',
        amount: 1850000,
        paymentStatus: 'Pending',
        paymentMode: 'NEFT',
      },
      {
        id: 'ORD-014',
        date: '2023-09-28',
        project: 'Business Hub One',
        unit: 'Office 804',
        amount: 3200000,
        paymentStatus: 'Completed',
        paymentMode: 'RTGS',
      },
    ],
  },
  {
    customerId: 'CUST003',
    name: 'Amit Patel',
    email: 'amit.patel@example.com',
    mobile: '9234567890',
    city: 'Ahmedabad',
    state: 'Gujarat',
    address: '789, SG Highway, Ahmedabad, Gujarat - 380015',
    profession: 'Doctor',
    status: 'Inactive',
    purchaseHistory: [
      {
        id: 'ORD-031',
        date: '2024-03-05',
        project: 'Sunrise Residency',
        unit: '3BHK Flat 504',
        amount: 1680000,
        paymentStatus: 'In Progress',
        paymentMode: 'Home Loan',
      },
    ],
  },
];

const CustomerHistory = () => {
  const history = useHistory();
  const location = useLocation();

  const payload = location.state?.customer;

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const customer = useMemo(() => {
    if (payload) {
      return payload;
    }

    const lookup = location.state?.openPurchaseHistoryFor;
    if (!lookup) return null;

    return fallbackCustomers.find((item) =>
      (lookup.customerId && item.customerId === lookup.customerId) ||
      (lookup.phone && item.mobile === lookup.phone) ||
      (lookup.email && item.email === lookup.email) ||
      (lookup.name && item.name === lookup.name),
    ) || null;
  }, [location.state, payload]);

  useEffect(() => {
    const fetchTransactions = async () => {
      const customerId = customer?._id || customer?.id;
      if (!customerId) return;

      setLoading(true);
      setError(null);
      try {
        const base = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${base}/api/transactions/payment/${customerId}`);
        const result = await response.json();

        if (result.success) {
          setTransactions(result.data || []);
        } else {
          setError(result.message || 'Failed to fetch transaction history');
        }
      } catch (err) {
        console.error('Error fetching transactions:', err);
        setError(err.message || 'An error occurred while fetching transactions');
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [customer]);

  const purchaseHistory = Array.isArray(customer?.purchaseHistory) ? customer.purchaseHistory : [];

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => history.goBack()} sx={{ bgcolor: '#fff', border: '1px solid #e0e0e0' }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
          Past Bookings
        </Typography>
      </Box>

      {!customer ? (
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <HistoryIcon sx={{ fontSize: 48, color: '#90a4ae', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>No customer selected</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please navigate here via the Customer or Lead pages to view detailed history.
          </Typography>
          <Button variant="contained" onClick={() => history.push('/app/members/Customer')}>
            Go to Customer List
          </Button>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 72, height: 72, bgcolor: '#536DFE', fontSize: 32, fontWeight: 700 }}>
                    {customer.name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{customer.name}</Typography>
                    <Chip
                      label={customer.status || 'Active'}
                      size="small"
                      sx={{
                        mt: 0.5,
                        fontWeight: 600,
                        bgcolor: (customer.status === 'Inactive') ? 'rgba(244, 67, 54, 0.15)' : 'rgba(76, 175, 80, 0.15)',
                        color: (customer.status === 'Inactive') ? '#d32f2f' : '#2e7d32',
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person sx={{ color: '#607d8b' }} />
                      <Typography variant="body2">ID: {customer.customerId}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone sx={{ color: '#4caf50' }} />
                      <Typography variant="body2">{customer.mobile || '-'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email sx={{ color: '#2196f3' }} />
                      <Typography variant="body2">{customer.email || '-'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ color: '#ff7043' }} />
                      <Typography variant="body2">{customer.city}, {customer.state}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon sx={{ color: '#536DFE' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Transaction History</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : transactions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No transaction history available for this customer.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {transactions.map((transaction) => (
                  <Grid item xs={12} key={transaction._id}>
                    <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `5px solid ${transaction.transactionType === 'deduct' ? '#f44336' : '#4caf50'}` }}>
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {transaction.receivedType?.replace(/([A-Z])/g, ' $1').trim()}
                              {transaction.paymentType && ` - ${transaction.paymentType.replace(/_/g, ' ')}`}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                Receipt ID: <strong>{transaction.receiptId || 'N/A'}</strong>
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Date: {transaction.receivedDate ? new Date(transaction.receivedDate).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                            {transaction.otherRemark && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                                Remark: {transaction.otherRemark}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: transaction.transactionType === 'deduct' ? '#d32f2f' : '#2e7d32'
                                }}
                              >
                                {transaction.transactionType === 'deduct' ? '-' : '+'} ₹{transaction.amount?.toLocaleString('en-IN')}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1, mt: 0.5, alignItems: 'center' }}>
                                <Chip
                                  label={transaction.mode}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 600, fontSize: '10px' }}
                                />
                                <Chip
                                  label={transaction.receiptStatus || 'Pending'}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    backgroundColor:
                                      transaction.receiptStatus === 'approved'
                                        ? 'rgba(76, 175, 80, 0.15)'
                                        : 'rgba(255, 193, 7, 0.15)',
                                    color:
                                      transaction.receiptStatus === 'approved'
                                        ? '#2e7d32'
                                        : '#ff9800',
                                  }}
                                />
                              </Box>
                              {transaction.refNo && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  Ref: {transaction.refNo} {transaction.bankName ? `| ${transaction.bankName}` : ''}
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon sx={{ color: '#536DFE' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Purchase History</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {purchaseHistory.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No purchase history available for this customer.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {purchaseHistory.map((purchase) => (
                  <Grid item xs={12} key={purchase.id}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {purchase.project}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {purchase.unit}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Order ID: {purchase.id} · {new Date(purchase.date).toLocaleDateString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                ₹{purchase.amount.toLocaleString('en-IN')}
                              </Typography>
                              <Chip
                                label={purchase.paymentStatus}
                                size="small"
                                sx={{
                                  mt: 0.5,
                                  fontWeight: 600,
                                  bgcolor:
                                    purchase.paymentStatus === 'Completed'
                                      ? 'rgba(76, 175, 80, 0.15)'
                                      : purchase.paymentStatus === 'Pending'
                                        ? 'rgba(255, 193, 7, 0.15)'
                                        : 'rgba(33, 150, 243, 0.15)',
                                  color:
                                    purchase.paymentStatus === 'Completed'
                                      ? '#2e7d32'
                                      : purchase.paymentStatus === 'Pending'
                                        ? '#ff9800'
                                        : '#1976d2',
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Payment Mode: {purchase.paymentMode}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default CustomerHistory;

